import { useState, useEffect } from 'react'
import { useLoaderData, useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { BookingService } from '../../services/booking.service'
import { VehicleService } from '../../services/vehicle.service'
import BookingCard from './BookingCard'
import style from './PastBookings.module.scss'
import { Bookings, VehicleForBookingResponse } from '../../types/api.types'
import CommonModal from '../../Components/Modal/Modal'
import { StoreInterface } from '../../store/store'
import { formatDate, formatTime } from './Booking.util'
import BookingDetailsModal from './BookingDetailsModal'

const bookingService = new BookingService()
const vehicleService = new VehicleService()

const PastBookings = (): JSX.Element => {
  const bookings: Bookings[] = (useLoaderData() as Bookings[]) || []
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedBooking, setSelectedBooking] = useState<Bookings | null>(null)
  const [vehiclesMap, setVehiclesMap] = useState<VehicleForBookingResponse[]>([])
  const [loadingVehicles, setLoadingVehicles] = useState(true)

  const { accessToken } = useSelector((store: StoreInterface) => store.profile)

  // Fetch all vehicles once on component mount
  useEffect(() => {
    const fetchAllVehicles = async () => {
      if (!accessToken) return

      setLoadingVehicles(true)
      try {
        const response = await vehicleService.getAllVehicles(
          {
            page: 1,
            limit: 1000,
            isActive: true,
          },
          accessToken,
        )

        setVehiclesMap(response.data.vehicles)
      } catch (error) {
        console.error('Error fetching vehicles:', error)
      } finally {
        setLoadingVehicles(false)
      }
    }

    fetchAllVehicles()
  }, [accessToken])

  // Open booking modal if bookingId is in URL params
  useEffect(() => {
    const bookingId = searchParams.get('bookingId')
    if (bookingId && bookings.length > 0) {
      const booking = bookings.find(b => b.id === bookingId)
      if (booking) {
        setSelectedBooking(booking)
      }
    }
  }, [bookings, searchParams])

  const handleCardClick = (booking: Bookings) => {
    setSelectedBooking(booking)
  }

  const handleCloseModal = () => {
    setSelectedBooking(null)
  }

  // Helper function to get vehicle name from the map
  const getVehicleName = (vehicleId: string): string => {
    if (loadingVehicles) return 'Loading...'
    const vehicle = vehiclesMap.find(v => v.id === vehicleId)
    return vehicle?.name || 'Unknown Vehicle'
  }

  const getVehicleData = (vehicleId: string): VehicleForBookingResponse | null => {
    if (loadingVehicles) return null
    return vehiclesMap.find(v => v.id === vehicleId) || null
  }

  return (
    <div className={style.pastBookings}>
      <div className="container">
        <h1>Booking History</h1>
        {bookings.length === 0 ? (
          <div className={style.emptyState}>
            <div className={style.emptyStateIcon}>📅</div>
            <h2 className={style.emptyStateTitle}>No Bookings Yet</h2>
            <p className={style.emptyStateMessage}>
              You haven't made any bookings yet. Start your journey by making your first
              reservation.
            </p>
            <a href="/" className={style.emptyStateButton}>
              Book Now
            </a>
          </div>
        ) : (
          <div className={`${style.pastBookings__row} flex-wrap`}>
            {bookings
              .sort(
                (a: Bookings, b: Bookings) =>
                  new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
              )
              .map((booking: Bookings) => {
                return (
                  <div
                    className={style.pastBookings__item}
                    key={booking.id}
                    onClick={() => handleCardClick(booking)}
                    style={{ cursor: 'pointer' }}
                  >
                    <BookingCard
                      details={booking}
                      getVehicleData={getVehicleData}
                      loadingVehicles={loadingVehicles}
                    />
                  </div>
                )
              })}
          </div>
        )}
      </div>

      {selectedBooking && (
        <BookingDetailsModal
          selectedBooking={selectedBooking}
          handleCloseModal={handleCloseModal}
          searchParams={searchParams}
          loadingVehicles={loadingVehicles}
          getVehicleName={getVehicleName}
          formatDate={formatDate}
          formatTime={formatTime}
        />
      )}
    </div>
  )
}
export default PastBookings

export async function getBookings(userId: string, token: string) {
  try {
    const res = await bookingService.getPastBookings()
    return res.data
  } catch (error) {
    console.error(error)
    return []
  }
}
